import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Redirect } from 'react-router-dom';

import type { FreeTextbooksSelectorButtonQuery } from '__free_textbooks_generated__/FreeTextbooksSelectorButtonQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import {
  getCurriculumSelectorUrl,
  getTextbookSelectorUrl,
} from 'ms-pages/FreeTextbooks/urls';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { fontFamily, fontSize, fontWeight, lineHeight } from 'ms-styles/base';
import { lanternColors } from 'ms-styles/colors';
import { sizeToHeight } from 'ms-ui-primitives/Button';
import LinkButton from 'ms-ui-primitives/Button/ReactRouterLinkButton';
import { VStack, VSpacer } from 'ms-ui-primitives/Stack';
import { useRouter } from 'ms-utils/hooks/useRouter';
import { loginUrl } from 'ms-utils/urls';

type Props = {
  syllabusId: string;
};

const TEXTBOOK_SELECTOR_LINK_SIZE = 'medium';

const TextbookLabel = styled.div({
  color: lanternColors.grey,
  fontFamily: fontFamily.body,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.title,
});

const TextbookSelectorLink = styled.div({
  // Offset the Button's tap area so that the spacing
  // between the elements is not affected
  margin: `${-0.3 * sizeToHeight[TEXTBOOK_SELECTOR_LINK_SIZE]}px 0`,
});

export function FreeTextbooksSelectorButton({ syllabusId }: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSelectorButton">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSelectorButtonInner
            fetchKey={fetchKey}
            syllabusId={syllabusId}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSelectorButtonInner({
  fetchKey,
  syllabusId,
}: Props & { fetchKey: string }) {
  const { trackStructEvent } = useSnowplow();
  const { history } = useRouter();

  const data = useLazyLoadQuery<FreeTextbooksSelectorButtonQuery>(
    graphql`
      query FreeTextbooksSelectorButtonQuery($syllabusId: ID!) {
        syllabus(id: $syllabusId) {
          title
          curriculum {
            id
            country {
              code
            }
          }
        }
      }
    `,
    { syllabusId },
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and the types are sound
        useCache: true,
      },
    },
  );
  const { syllabus } = data;

  if (syllabus === null) {
    if (window.inSSR !== true) {
      window.location.assign(loginUrl);
      return null;
    }
    return <Redirect to={loginUrl} />;
  }

  const curriculumSelectorUrl =
    syllabus && syllabus.curriculum.country != null
      ? getTextbookSelectorUrl({
          countryCode: syllabus.curriculum.country.code,
          curriculumId: syllabus.curriculum.id,
        })
      : getCurriculumSelectorUrl({ countryCode: 'AU' });

  return (
    <VStack>
      <TextbookLabel>{syllabus.title}</TextbookLabel>
      <VSpacer height={12} />
      <TextbookSelectorLink>
        <LinkButton
          onClick={() => {
            trackStructEvent({
              category: 'free_textbook',
              action: 'open_textbook_selector',
            });
            history.push(curriculumSelectorUrl);
          }}
          to={curriculumSelectorUrl}
          padding={0}
          size={TEXTBOOK_SELECTOR_LINK_SIZE}
        >
          Change textbook
        </LinkButton>
      </TextbookSelectorLink>
    </VStack>
  );
}
